<script setup>
import { relativeTimeDifference } from '@0x234/common-util/datetime';
import { useDialogManager } from '@0x234/common-vue/composables/dialog-manager';
import { useNotifyManager } from '@0x234/common-vue/composables/notify-manager';
import ShortlistJobBtn from 'components/btn/ShortlistJobBtn.vue';
import CompanyProfileCard from 'components/CompanyProfileCard.vue';
import DialogLogoHeaderSection from 'components/DialogLogoHeaderSection.vue';
import JobHeaderInfo from 'components/JobHeaderInfo.vue';
import JobStatusChip from 'components/JobStatusChip.vue';
import { useBreakpoint } from 'composables/breakpoint';
import equal from 'fast-deep-equal';
import { useQuasar } from 'quasar';
import { ResourceNoLongerAvailableError } from 'src/errors/client-error';
import AppRoute from 'src/router/app-route';
import { Route } from 'src/router/routes';
import { formattedRemuneration } from 'src/utils/job-utils';
import { useAuthStore } from 'stores/auth-store';
import { useJobApplicationStore } from 'stores/job-application-store';
import { computed, defineAsyncComponent, ref } from 'vue';
import { useRouter } from 'vue-router';

const $emit = defineEmits(['job-scope:click']);

const $props = defineProps({
    job: Object,
    activeJobScope: Object,
    inSearchContext: Boolean,
    inPreviewContext: Boolean
});

const $q = useQuasar();
const $router = useRouter();
const authStore = useAuthStore();
const breakpoint = useBreakpoint();
const dialogManager = useDialogManager();
const jobApplicationStore = useJobApplicationStore();
const notifyManager = useNotifyManager();

let externalApplicationRedirectTimer = null;
const externalApplicationRedirectDuration = 5000;
const externalApplicationRedirectProgress = ref(1);
const externalApplicationDialog = ref(false);

const scopeLimit = ref(1);

const activeJobScope = computed(() => {
    return $props.activeJobScope || $props.job.scopes[0];
});

/**
 * A computed property that returns an ordered list of job scopes. The list is based on the job's scopes from the
 * component's props. If an active job scope is defined, this scope is moved to the front of the list to ensure the
 * active job scope location is displayed in the correct position.
 */
const orderedJobScopes = computed(() => {
    const jobScopes = [...$props.job.scopes];
    const activeScope = activeJobScope.value;
    if (jobScopes[0] !== activeScope) {
        const index = jobScopes.findIndex(scope => equal(scope, activeScope));
        if (index !== -1) {
            jobScopes.splice(index, 1);
            jobScopes.unshift(activeScope);
        }
    }
    return jobScopes;
});

const formattedRemunerationRange = computed(() => {
    const contributions = activeJobScope.value.remuneration.contributions
        ? ` ${activeJobScope.value.remuneration.contributions}`
        : '';
    return formattedRemuneration(
        activeJobScope.value.location.currencyCode,
        activeJobScope.value.remuneration.range[0],
        activeJobScope.value.remuneration.range[1],
        activeJobScope.value.remuneration.cycle
    ) + contributions;
});

const relativeAppliedAt = computed(() => {
    return $props.job.appliedAt
        ? relativeTimeDifference(new Date($props.job.appliedAt))
        : 'just now';
});

const relativePublishedAt = computed(() => {
    return $props.job.publishedAt
        ? relativeTimeDifference(new Date($props.job.publishedAt))
        : 'just now';
});

const fullClassificationLabel = computed(() => {
    return $props.job.parentClassification
        ? `${$props.job.parentClassification.name} → ${$props.job.classification.name}`
        : $props.job.classification.name;
});

function toggleScopeLimit() {
    scopeLimit.value = scopeLimit.value !== null
        ? null
        : 1;
}

function gotoParentRoute() {
    const currentRoute = $router.currentRoute.value;
    currentRoute.matched.length > 1
        ? $router.push(currentRoute.matched[currentRoute.matched.length - 2].path)
        : $router.push(AppRoute.build(Route.SearchResultsPage));
}

function openStandaloneJobScope(jobScopeId) {
    if ($props.inPreviewContext) {
        return;
    }
    jobScopeId ??= activeJobScope.value.id;
    if ($props.inSearchContext) {
        window.open(resolveJobPageUrl(jobScopeId), '_blank');
        return;
    }
    $router.push(AppRoute.build(Route.JobPage, { jobScopeId }));
}

function resolveJobPageUrl(jobScopeId) {
    if (!jobScopeId) {
        // If no job scope ID is provided, i.e. it hasn't been saved yet, then return the current path to avoid breaking
        // the component from displaying.
        return $router.currentRoute.value.fullPath;
    }
    return $router.resolve(AppRoute.build(Route.JobPage, { jobScopeId })).href;
}

function openSocialShareDialog() {
    if ($props.inPreviewContext) {
        return;
    }
    const jobScopeId = activeJobScope.value.id;
    const relativePath = $router.resolve(AppRoute.build(Route.JobPage, { jobScopeId })).href;
    const absoluteUrl = new URL(relativePath, window.location.origin).href;
    dialogManager.openDialogComponent(defineAsyncComponent(() => import('components/SocialShareDialog.vue')), {
        url: absoluteUrl,
        title: `Exciting Job Opportunity: ${$props.job.title} at ${$props.job.company.name}`,
        message: `Check out this ${$props.job.title} role at ${$props.job.company.name}!`,
        instruction:
            'Help spread the word about this great job opportunity! Choose your preferred platform below to share it with your professional network. Your share could' +
            ' help someone find their dream job!'
    });
}

function openJobReportDialog() {
    if ($props.inPreviewContext) {
        return;
    }
    if (!authStore.isUserAuthenticated) {
        dialogManager.openDialogComponent(defineAsyncComponent(() => import('components/UserLoginDialog.vue')), {
            headline: 'Help maintain the quality of our platform',
            message: 'To report this job listing, please log in or create an account. We require users to be logged in to ensure reports are accountable. Your identity will always '
                + 'be kept confidential from the reported party, but we may need to contact you for further information if necessary.'
        });
        return;
    }
    dialogManager.openDialogComponent(defineAsyncComponent(() => import('components/JobReportDialog.vue')), {
        jobScopeId: activeJobScope.value.id,
        jobTitle: $props.job.title,
        companyName: $props.job.company.name
    });
}

function openJobManagerListingPage() {
    if (!$props.inPreviewContext) {
        $router.push(AppRoute.build(Route.JobManagerListingPage, { jobId: $props.job.id }));
    }
}

function openJobApplication() {
    if ($props.inPreviewContext) {
        return;
    }
    if (!authStore.isUserAuthenticated) {
        dialogManager.openDialogComponent(defineAsyncComponent(() => import('components/UserLoginDialog.vue')), {
            headline: 'Your new opportunity awaits...',
            message: 'Create your free account to proceed with this application and unlock the full Xprt experience. ' +
                'Members can save favourite jobs, build their professional profile, and stay updated on new ' +
                'opportunities in their field.'
        });
        return;
    }
    if ($props.job.sourceUrl || $props.job.extApplication) {
        openExternalApplicationPageInNewTab();
    } else {
        openJobApplicationWizardPage();
    }
}

async function openExternalApplicationPageInNewTab() {
    const redirectUrl = await getExternalApplicationRedirectUrl();
    if (!redirectUrl) {
        return;
    }
    externalApplicationDialog.value = true;
    startRedirectCountdown(() => {
        // To prevent the referrer from being sent, the 'noreferrer' must be set. The only way to do this is by creating
        // a hidden link element and dispatching a click event.
        const a = document.createElement('a');
        a.href = redirectUrl;
        a.target = '_blank';
        a.rel = 'noreferrer';
        a.style.display = 'none';
        document.body.appendChild(a);
        a.dispatchEvent(new MouseEvent('click'));
        document.body.removeChild(a);
        externalApplicationDialog.value = false;
    });
}

function openJobApplicationWizardPage() {
    const jobScopeId = activeJobScope.value.id;
    $router.push(AppRoute.build(Route.JobApplicationWizardPage, { jobScopeId, step: 1 }));
}

async function getExternalApplicationRedirectUrl() {
    try {
        $q.loading.show({ message: 'Checking job availability...' });

        // For any external application, we need to get the URL from the backend
        return (await jobApplicationStore.loadActiveJobApplication(activeJobScope.value.id)).url;
    } catch (err) {
        if (err instanceof ResourceNoLongerAvailableError) {
            dialogManager.promptNegative({
                html: true,
                title: 'Job No Longer Available',
                message: 'We\'re really sorry, but this job listing appears to have been removed or is no longer available. Sometimes listings are removed early without prior notice.'
                    + '<br/><br/>We apologise for any inconvenience and invite you to browse other opportunities that might interest you.',
                cancel: false
            });
            return;
        }
        notifyManager.notifyError('Something went wrong while checking the job availability, please try again.');
    } finally {
        $q.loading.hide();
    }
}

function startRedirectCountdown(cb) {
    const stepDuration = 10;
    const startTime = Date.now();
    externalApplicationRedirectProgress.value = 1;
    externalApplicationRedirectTimer = setInterval(() => {
        const timeElapsed = Date.now() - startTime;
        externalApplicationRedirectProgress.value = Math.max(0, 1 - (timeElapsed / externalApplicationRedirectDuration));
        if (timeElapsed >= externalApplicationRedirectDuration) {
            clearInterval(externalApplicationRedirectTimer);
            externalApplicationRedirectTimer = null;
            cb();
        }
    }, stepDuration);
}

function cancelExternalApplication() {
    if (externalApplicationRedirectTimer) {
        clearTimeout(externalApplicationRedirectTimer);
        externalApplicationRedirectTimer = null;
    }
    externalApplicationDialog.value = false;
}
</script>

<template>
    <div v-if="job" class="job-detail">
        <div class="flex q-mb-md no-wrap">
            <JobHeaderInfo
                :job
                avatar-size="150px"
                job-title-element="h3"
                job-title-class="text-h3 q-mt-none q-mb-sm"
                company-name-element="h5"
                company-name-class="text-h5 q-mt-none q-mb-none text-weight-regular"
            />
            <div v-if="inSearchContext" class="q-ml-auto q-mr-none">
                <q-btn @click="gotoParentRoute()" icon="sym_r_close" aria-label="Close job preview" padding="6px" flat/>
            </div>
        </div>
        <template v-if="job.appliedAt">
            <div class="alert alert-info q-mb-lg q-mt-lg">
                <div class="flex row no-wrap items-center q-gutter-x-md">
                    <q-icon name="sym_r_info" size="sm" color="accent"/>
                    <p class="q-my-none">
                        You applied to this job {{ relativeAppliedAt }}.
                    </p>
                </div>
            </div>
        </template>
        <div class="job-details row q-col-gutter-sm q-mb-lg">
            <div v-if="job.owner" class="col-12 flex items-center no-wrap">
                <q-icon name="sym_r_vital_signs" size="sm"/>
                <JobStatusChip :status="job.status" class="q-ml-sm q-my-none"/>
                <div class="flex row items-center text-grey-6 text-italic q-ml-md no-wrap">
                    <q-icon name="sym_r_visibility" size="xs" class="q-mr-xs"/>
                    <p class="text-caption q-mb-none">Only visible to you.</p>
                </div>
            </div>
            <div class="col-12 flex items-center">
                <q-icon name="sym_r_location_pin" class="self-start" size="sm"/>
                <div class="flex column items-center q-gutter-y-sm">
                    <div v-for="(s, i) of orderedJobScopes.slice(0, scopeLimit ?? orderedJobScopes.length)" :key="s.id" class="q-ml-sm block">
                        <template v-if="i > 0">
                            <span>&#8735;&nbsp;</span>
                            <template v-if="inPreviewContext">
                                <a @click.prevent="$emit('job-scope:click', s)" href="#">{{ s.location.name }}</a>
                            </template>
                            <template v-else>
                                <a @click.prevent="openStandaloneJobScope(s.id)" :href="resolveJobPageUrl(s.id)">{{ s.location.name }}</a>
                            </template>
                        </template>
                        <template v-else>
                            <span>{{ s.location.shortName }}</span>
                        </template>
                        <template v-if="i === 0 && job.scopes.length > 1">
                            <small><a @click.prevent="toggleScopeLimit" href="#" class="q-ml-lg">
                                <template v-if="scopeLimit !== null">Show other locations</template>
                                <template v-else>Hide other locations</template>
                            </a></small>
                        </template>
                    </div>
                </div>
            </div>
            <div class="col-12 flex items-center no-wrap">
                <q-icon name="sym_r_money_bag" size="sm"/>
                <span class="q-ml-sm">{{ formattedRemunerationRange }}</span>
            </div>
            <div class="col-12 flex items-center no-wrap">
                <q-icon name="sym_r_domain" size="sm"/>
                <span class="q-ml-sm">{{ fullClassificationLabel }}</span>
            </div>
            <div class="col-12 flex items-center no-wrap">
                <q-icon name="sym_r_schedule" size="sm"/>
                <span class="q-ml-sm">{{ job.employmentType }}</span>
            </div>
            <div class="col-12 flex items-center no-wrap">
                <q-icon name="sym_r_home_work" size="sm"/>
                <span class="q-ml-sm">{{ activeJobScope.workLocation }}</span>
            </div>
        </div>
        <div class="job-actions q-mt-lg q-gutter-x-md">
            <template v-if="job.owner">
                <q-btn @click="openJobManagerListingPage()" color="primary" label="Manage" aria-label="Manage job" size="lg"/>
            </template>
            <template v-else-if="job.appliedAt">
                <q-btn @click="openJobApplicationWizardPage()" color="primary" label="Review application" aria-label="Review job application" size="lg">
                    <q-tooltip :delay="1000">You applied to this job {{ relativeAppliedAt }}.</q-tooltip>
                </q-btn>
            </template>
            <template v-else>
                <q-btn
                    @click="openJobApplication()"
                    :icon-right="job.sourceUrl || job.extApplication ? 'sym_r_open_in_new' : undefined"
                    color="primary"
                    label="Apply Now"
                    aria-label="Apply for this job"
                    size="lg"
                />
            </template>
            <ShortlistJobBtn :job-scope="activeJobScope" :in-preview-context/>
            <q-btn
                @click="openStandaloneJobScope()"
                v-if="inSearchContext"
                color="primary"
                icon="sym_r_open_in_new"
                aria-label="Open job in new tab"
                padding="6px"
                flat
            >
                <q-tooltip :delay="1000">Open in new tab</q-tooltip>
            </q-btn>
            <q-btn @click="openSocialShareDialog" color="primary" icon="sym_r_share" aria-label="Share this job" padding="6px" flat>
                <q-tooltip :delay="1000">Share this job</q-tooltip>
            </q-btn>
            <q-btn v-if="!job.owner" @click="openJobReportDialog" color="primary" icon="sym_r_report" aria-label="Report this job" padding="6px" flat>
                <q-tooltip :delay="1000">Report this job</q-tooltip>
            </q-btn>
        </div>
        <p class="text-italic q-mt-lg">Posted {{ relativePublishedAt }}</p>
        <q-separator class="q-mt-lg q-mb-xl gradient-separator"/>
        <div class="job-description q-mt-lg">
            <div v-if="job.descriptionHtml" v-html="job.descriptionHtml"></div>
            <div v-else class="ease-out-overlay">
                <q-skeleton v-for="() in Array(14)" :width="`${Math.floor(Math.random() * 6) + 95}%`" type="text"/>
            </div>
        </div>
        <q-separator class="q-mt-lg gradient-separator"/>
        <CompanyProfileCard :company="job.company" class="q-mt-xl q-mb-md"/>
    </div>
    <q-dialog v-model="externalApplicationDialog" class="job-application-redirect-dialog" :maximized="breakpoint.lt.sm" persistent>
        <q-card class="card">
            <q-card-section>
                <DialogLogoHeaderSection hide-close-btn/>
            </q-card-section>
            <q-card-section class="text-center">
                <h5 class="q-mt-sm q-mb-lg">Redirecting to Job Application...</h5>
                <template v-if="$props.job.sourceUrl">
                    <!-- SEEK-specific message -->
                    <p>This job was originally posted on SEEK. To proceed, we need to redirect you to their application page, which will open in a new tab.</p>
                    <p><strong>Please ensure your browser allows the popup for the application to open.</strong></p>
                    <p>Once you've completed the application there, please return to this tab to continue exploring more job opportunities.</p>
                </template>
                <template v-else>
                    <!-- Generic external application message -->
                    <p>This job requires you to complete the application on an external website. We'll redirect you there in a moment.</p>
                    <p><strong>Please ensure your browser allows the popup for the application to open.</strong></p>
                    <p>Once you've completed the application, please return to this tab to continue exploring more job opportunities.</p>
                </template>
                <p class="q-mb-none">We wish you the best of luck with your application.</p>
            </q-card-section>
            <q-card-actions align="center" class="q-pa-md">
                <q-btn @click="cancelExternalApplication" label="Wait, I've changed my mind" aria-label="Cancel job application" flat v-close-popup/>
            </q-card-actions>
            <q-linear-progress :value="externalApplicationRedirectProgress" color="primary" size="3px" instant-feedback/>
        </q-card>
    </q-dialog>
</template>

<style lang="scss" scoped>
.job-application-redirect-dialog .q-card {
    min-width: 350px;
    max-width: 600px;
    width: 100%;
}
</style>
